// src/companies/kontoJson.js — „Аналитичности и номенклатури" от JSON
// експорта на Конто (ПРОМТ-45). Само за админ, в „Мои фирми".
//
// Експортът е папка с дата (`E:\счетоводни схеми\<ГГГГ-ММ-ДД>\`) с по един
// `<ЕИК>.json` на фирма (0,3–10 MB). Файловете се пращат ЕДИН ПО ЕДИН —
// 100 наведнъж не се държат в паметта нито тук, нито на сървъра — и преди
// пращането се олекотяват (контрагентите без адресите, без суровите полета):
// сървърът чете само това, което му трябва (services/kontoJsonImport.js).
// Пътят е като при другите вносове: преглед (нищо не се пише) → „Внеси".
// `_summary.json` и другите файлове с „_" отпред не се пращат — те са за хора.

import { el, clear } from '../shared/dom.js';
import { openModal, closeModal } from '../shared/modal.js';
import { uploadRaw, dropZone } from '../shared/upload.js';

const n = (x) => Number(x || 0).toLocaleString('bg-BG');
const plural = (k, one, many) => n(k) + ' ' + (Number(k) === 1 ? one : many);
const ROLE = { customer: 'клиент', supplier: 'доставчик' };

// Само каквото сървърът чете — АТРИЯ е 10 MB, олекотена е под една десета.
export function slimFirm(o) {
  const src = o && typeof o === 'object' ? o : {};
  const partner = new Set();
  for (const a of src.accounts || []) for (const l of (a && a.levels) || []) if (l && l.is_partner) partner.add(String(l.nomenclature));
  const f = src.firm || {};
  return {
    export: { date: (src.export && src.export.date) || '' },
    firm: { name: f.name, eik: f.eik, konto_company_id: f.konto_company_id, vat_registered: f.vat_registered },
    accounts: (src.accounts || []).map((a) => ({
      acc_no: a.acc_no, name: a.name,
      levels: (a.levels || []).map((l) => ({
        level: l.level, nomenclature: l.nomenclature, nomenclature_name: l.nomenclature_name, value_source: l.value_source,
        data_type: l.data_type, is_partner: l.is_partner,
        used_codes: (l.used_codes || []).map((u) => ({ code: u.code, lines: u.lines, last_date: u.last_date })),
      })),
    })),
    nomenclatures: (src.nomenclatures || []).map((x) => ({
      nomenclature: x.nomenclature, name: x.name, code_length: x.code_length, kind: x.kind,
      elements: partner.has(String(x.nomenclature)) || x.kind === 'контрагенти' ? [] : (x.elements || []).map((e) => ({ code: e.code, name: e.name, active: e.active, used_lines: e.used_lines })),
    })),
    contractors: (src.contractors || []).map((c) => ({
      nomenclature: c.nomenclature, code: c.code, name: c.name, eik: c.eik, vat_number: c.vat_number, country: c.country, type: c.type, active: c.active,
      accounts: (c.accounts || []).map((a) => ({ lines: a.lines, last_date: a.last_date })),
    })),
    vat: { deal_types: (src.vat && src.vat.deal_types) || [] },
    other: { measures: (src.other && src.other.measures) || [], document_types: (src.other && src.other.document_types) || [] },
  };
}

// Кои файлове от избраното са на фирми: `<ЕИК>.json`, без „_summary.json".
export function firmFiles(list) {
  return [...(list || [])].filter((f) => /\.json$/i.test(f.name) && !/^_/.test(f.name)).sort((a, b) => a.name.localeCompare(b.name));
}

export function kontoJsonDialog(opts) {
  let files = [];
  let results = []; // по файл: { file, data?, error? }
  const live = {};  // ЕИК на фирмата → { 'роля|ключ': код }
  const out = el('div', { class: 'imp-out', 'aria-live': 'polite' });
  const foot = el('div', {});
  const closeBtn = (text) => el('button', { class: 'btn ghost', type: 'button', text: text || 'Отказ', onClick: closeModal });

  async function send(mode, f) {
    const o = JSON.parse(await f.text());
    const eik = String((o && o.firm && (o.firm.eik || o.firm.konto_company_id)) || '');
    const body = new Blob([JSON.stringify({ file: slimFirm(o), live: live[eik] || {} })], { type: 'application/octet-stream' });
    return uploadRaw('/api/companies/konto-json?mode=' + mode, Object.assign(body, { name: f.name }), { contentType: 'application/octet-stream' });
  }

  async function run(mode) {
    clear(out); clear(foot);
    const bar = el('progress', { max: String(files.length), value: '0', 'aria-label': 'Напредък на вноса' });
    const msg = el('p', { class: 'muted' });
    out.append(msg, bar);
    const next = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      msg.textContent = (mode === 'apply' ? 'Записвам ' : 'Чета ') + (i + 1) + ' от ' + files.length + ' — ' + f.name + '…';
      try { next.push({ file: f.name, data: await send(mode, f) }); }
      catch (e) { next.push({ file: f.name, error: e.message || 'Файлът не можа да се прочете.' }); }
      bar.value = String(i + 1);
    }
    results = next;
    render(mode === 'apply');
  }

  function render(done) {
    clear(out); clear(foot);
    const ok = results.filter((r) => r.data);
    const bad = results.filter((r) => r.error);
    const firmRows = ok.map((r) => {
      const d = r.data;
      const a = (d.analytics && d.analytics.firms && d.analytics.firms[0]) || null;
      const s = (d.sync && d.sync.firms && d.sync.firms[0]) || null;
      const diff = (a && a.diff) || { added: [], gone: [], renamed: [] };
      return { d, a, s, diff, isNew: !a && !!(d.analytics && d.analytics.missing && d.analytics.missing.length) };
    });
    const tot = firmRows.reduce((t, x) => ({
      accounts: t.accounts + ((x.a && x.a.accounts) || 0), elements: t.elements + ((x.a && x.a.elements) || 0),
      contacts: t.contacts + ((x.s && x.s.create_count) || 0), codes: t.codes + ((x.s && (x.s.counts.filled + x.s.counts.fixed)) || 0),
    }), { accounts: 0, elements: 0, contacts: 0, codes: 0 });
    out.appendChild(el('p', { class: 'imp-lead', text: (done ? 'Записани са ' : 'Ще се запишат ') + plural(ok.length, 'фирма', 'фирми') + ': ' + plural(tot.accounts, 'сметка', 'сметки') + ' с нивата им, ' + plural(tot.elements, 'елемент', 'елемента') + ' на номенклатури, ' + plural(tot.codes, 'шифър', 'шифъра') + ' на контрагенти и ' + plural(tot.contacts, 'нов контакт', 'нови контакта') + '.' }));
    if (firmRows.length) {
      out.appendChild(el('div', { class: 'table-wrap', style: 'max-height:300px;overflow:auto' }, el('table', {},
        el('thead', {}, el('tr', {}, ...['Фирма', 'Експорт', 'Сметки', 'Елементи', 'Нови / изчезнали / преименувани', 'Шифри', 'Нови контакти'].map((t, i) => el('th', { scope: 'col', class: i >= 2 ? 'num' : null, text: t })))),
        el('tbody', {}, ...firmRows.map((x) => el('tr', {},
          el('td', { text: (x.a ? x.a.name : x.d.name) + (x.isNew ? ' (нова — ще се създаде)' : '') }),
          el('td', { text: x.d.export_date || '' }),
          el('td', { class: 'num', text: n(x.a ? x.a.accounts : 0) }),
          el('td', { class: 'num', text: n(x.a ? x.a.elements : 0) }),
          el('td', { class: 'num', text: x.diff.added.length + ' / ' + x.diff.gone.length + ' / ' + x.diff.renamed.length }),
          el('td', { class: 'num', text: n(x.s ? x.s.counts.filled + x.s.counts.fixed : 0) }),
          el('td', { class: 'num', text: n(x.s ? x.s.create_count : 0) })))))));
    }
    // За решение: един ЕИК с няколко кода в една номенклатура — кой е живият.
    const dups = firmRows.flatMap((x) => ((x.s && x.s.dup_konto) || []).map((dp) => ({ x, dp })));
    if (dups.length) {
      const box = el('details', { class: 'ks-more', open: !done }, el('summary', {}, el('span', { text: 'За решение' }), ' — един контрагент с няколко кода в Конто (' + dups.length + ')'),
        el('p', { class: 'muted', text: 'Предложен е кодът с повече записвания (после — с по-новото движение). Експортът към Конто ползва само избрания; другите се пазят в контакта, за да се разпознава историята.' }));
      const list = el('ul', {});
      for (const { x, dp } of dups.slice(0, 300)) {
        const eik = x.d.eik;
        const s = el('select', { disabled: done, 'aria-label': 'Живият код за ' + dp.name + ' (' + ROLE[dp.role] + ')' },
          ...(dp.codes || [{ code: dp.kept }]).map((c) => el('option', { value: c.code, text: c.code + (c.lines != null ? ' — ' + plural(c.lines, 'записване', 'записвания') : '') + (c.last ? ', последно ' + c.last : '') })));
        s.value = (live[eik] && live[eik][dp.role + '|' + dp.key]) || dp.kept;
        s.addEventListener('change', () => { live[eik] = live[eik] || {}; live[eik][dp.role + '|' + dp.key] = s.value; });
        list.appendChild(el('li', {}, el('span', { text: (x.a ? x.a.name : x.d.name) + ': ' + dp.name + ' (' + ROLE[dp.role] + ', ЕИК ' + dp.key + ') ' }), s));
      }
      box.appendChild(list);
      out.appendChild(box);
    }
    // Разликите спрямо предишния внос (по фирма).
    const diffs = firmRows.filter((x) => x.diff.added.length || x.diff.gone.length || x.diff.renamed.length);
    if (diffs.length) {
      const lines = [];
      for (const x of diffs) {
        const nm = x.a ? x.a.name : x.d.name;
        for (const e of x.diff.added.slice(0, 40)) lines.push(nm + ': нов ' + e.nom + '/' + e.code + ' „' + e.name + '“');
        for (const e of x.diff.gone.slice(0, 40)) lines.push(nm + ': изчезнал ' + e.nom + '/' + e.code + ' „' + e.name + '“ — остава неактивен');
        for (const e of x.diff.renamed.slice(0, 40)) lines.push(nm + ': ' + e.nom + '/' + e.code + ' „' + e.from + '“ → „' + e.to + '“');
      }
      out.appendChild(el('details', { class: 'ks-more' }, el('summary', {}, el('span', { text: 'Разлики спрямо предишния внос' }), ' (' + lines.length + ')'),
        el('ul', {}, ...lines.slice(0, 400).map((t) => el('li', { text: t })))));
    }
    const conflicts = firmRows.flatMap((x) => ((x.s && x.s.conflicts) || []).map((c) => (x.a ? x.a.name : x.d.name) + ': „' + c.name + '“ — в CRM-а ' + c.crm + ', в Конто ' + c.konto));
    if (conflicts.length) {
      out.appendChild(el('details', { class: 'ks-more' }, el('summary', {}, el('span', { text: 'Различни шифри' }), ' — не се сменят (' + conflicts.length + ')'),
        el('ul', {}, ...conflicts.slice(0, 300).map((t) => el('li', { text: t }))),
        el('p', { class: 'muted', text: 'Шифър, попълнен в CRM-а, не се сменя без изрично решение — сменете го в контакта, ако Конто е прав.' })));
    }
    const notes = [];
    for (const x of firmRows) {
      const nm = x.a ? x.a.name : x.d.name;
      for (const w of x.d.warnings || []) notes.push(nm + ': ' + w.text);
      if ((x.d.measures_missing || []).length) notes.push(nm + ': в Конто няма мерките ' + x.d.measures_missing.map((m) => m.code + ' (' + m.label + ')').join(', ') + ' — стоки с тези мерки не могат да се изнесат.');
      if (x.d.deal_proposal && x.d.deal_proposal.none) notes.push(nm + ': няма видове сделка по ДДС — фирмата не е по ЗДДС.');
    }
    if (notes.length) out.appendChild(el('div', { class: 'ks-note sev-warn', role: 'status' }, ...notes.slice(0, 50).map((t) => el('p', { text: t }))));
    if (bad.length) out.appendChild(el('div', { class: 'sev-warn', role: 'alert' }, el('p', { text: 'Не са прочетени (' + bad.length + '):' }), el('ul', {}, ...bad.map((b) => el('li', { text: b.file + ' — ' + b.error })))));
    if (done) { foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Готово', onClick: closeModal })); return; }
    foot.appendChild(closeBtn());
    if (ok.length) foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Внеси', style: 'margin-left:8px', onClick: () => { files = files.filter((f) => ok.some((r) => r.file === f.name)); run('apply'); } }));
  }

  function pick(list) {
    const picked = firmFiles(list);
    clear(out);
    if (!picked.length) { out.appendChild(el('p', { class: 'sev-warn', role: 'alert', text: 'В избраното няма файлове на фирми (<ЕИК>.json).' })); return; }
    files = picked;
    run('preview');
  }
  const zone = dropZone({
    id: 'konto-json-files', title: 'Файловете <ЕИК>.json от експорта на Конто', accept: '.json,application/json', multiple: true,
    hint: 'Изберете всички файлове от папката с датата (напр. E:\\счетоводни схеми\\2026-11-02\\) — или една фирма. „_summary.json“ се прескача. Нищо не се записва, преди да натиснете „Внеси“.',
    onFiles: pick,
  });
  const dirIn = el('input', { type: 'file', class: 'sr-only', id: 'konto-json-dir', webkitdirectory: '', directory: '', multiple: 'multiple' });
  dirIn.addEventListener('change', () => { pick(dirIn.files); dirIn.value = ''; });
  const dirBtn = el('label', { class: 'btn sm ghost', for: 'konto-json-dir', tabindex: '0', text: 'Избери цялата папка',
    onKeydown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); dirIn.click(); } } });
  const body = el('div', { class: 'imp-dialog' },
    el('p', { class: 'muted', text: 'За всяка фирма от Конто: как е разбита всяка сметка (нивата на партидата), елементите на номенклатурите (статии, вид дейност, проект, мерки), контрагентите (шифрите по ЕИК) и видовете сделка по ДДС. Вносът се повтаря с всеки нов експорт: новото се добавя, изчезналото от Конто остава като неактивно, статиите, дадени от CRM-а, остават.' }),
    zone, el('p', {}, dirBtn, dirIn),
    opts && opts.onOld ? el('p', { class: 'muted' }, 'Имате трите таблици xlsx от по-рано? ', el('button', { class: 'btn sm ghost', type: 'button', text: 'Стар формат (xlsx)…', onClick: () => { closeModal(); opts.onOld(); } })) : null,
    out);
  openModal({ title: 'Аналитичности и номенклатури от Конто', body, foot, wide: true });
  foot.appendChild(closeBtn());
}
